import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import _ from 'lodash';
import { map } from 'rxjs';
import { OrderStatusFilterService } from 'src/app/services/filters/order-status-filter.service';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { DateFilterListComponent } from 'src/app/shared/components/date-filter-list/date-filter-list.component';
import { FilterListComponent } from 'src/app/shared/components/filter-list/filter-list.component';
import { IdsFilterListComponent } from 'src/app/shared/components/ids-filter-list/ids-filter-list.component';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { OrderQueries } from 'src/app/shared/types/get-query';
import { Paginable } from 'src/app/shared/types/paginable';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { Order, OrderFilters, OrderStatus } from '../../../models/new/order';
import { Status } from '../../../models/new/status';
import { OrderMapper } from '../../../services/order-mapper';
import { DateRange } from '../../../models/new/dateRange';
import { SelectedStylesModalComponent } from '../selected-styles-modal/selected-styles-modal.component';
import { Warehouse } from 'src/app/shared/types/warehouse.model';
import { Style } from '../../../models/new/style';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { AddToOrderConfirmModalComponent } from '../add-to-order-confirm-modal/add-to-order-confirm-modal.component';
import { Router } from '@angular/router';

@Component({
	selector: 'bt-add-to-order-choose-modal',
	standalone: false,
	templateUrl: './add-to-order-choose-modal.component.html',
	styleUrl: './add-to-order-choose-modal.component.scss'
})
export class AddToOrderChooseModalComponent implements OnInit {
	orders: Paginable<Order> = new Paginable<Order>();
	orderFilters = new OrderFilters();
	orderStatus = OrderStatus;
	loading = false;
	readonly dialogRef = inject(MatDialogRef<AddToOrderChooseModalComponent>);
	readonly data = inject(MAT_DIALOG_DATA);

	constructor(
		private graphQlService: GraphQLService,
		private dialog: MatDialog,
		private utilitiesService: UtilitiesService,
		private orderStatusFilterService: OrderStatusFilterService,
		private orderMapper: OrderMapper,
		private router: Router
	) {}

	ngOnInit(): void {
		this.orderFilters.page = 1;
		this.orderFilters.size = 10;

		this.orders.currentPage = 1;
		this.orders.size = 10;

		this.getOrders();
	}

	getOrders(): void {
		this.loading = true;

		this.orders.currentPage = this.orderFilters.page || 1;
		this.orders.size = this.orderFilters.size || 10;
		this.orderFilters.statuses = [new Status({ id: OrderStatus.Draft, name: '' })];
		this.orderFilters.warehouses = [new Warehouse({ id: this.data.warehouse?.id, code: '', description: '' })];

		const pagination = this.orders.buildQuery(this.orders.currentPage);

		this.graphQlService
			.getData<any>(OrderQueries.GET_ORDERS, {
				where: this.orderMapper.toWhereClause(this.orderFilters),
				...pagination
			})
			.pipe(
				map((res: any) => {
					const orders = res.data.orders;

					return new Paginable<Selectable<Order>>({
						items: orders.nodes.map((node: any) => ({
							...this.orderMapper.toOrderSearch(node),
							selected: false
						})),
						totalCount: orders.totalCount,
						currentPage: this.orders.currentPage,
						size: this.orders.size
					});
				})
			)
			.subscribe({
				next: (data: Paginable<Selectable<Order>>) => {
					this.orders = data;
					this.loading = false;
				},
				error: (err) => {
					this.utilitiesService.showSnackbar('Error fetching orders', SnackbarType.Error);

					this.loading = false;
					console.error(err);
				}
			});
	}

	openSelectedStylesModal() {
		this.dialog
			.open(SelectedStylesModalComponent, {
				data: {
					styles: this.data.styles
				}
			})
			.afterClosed()
			.subscribe({
				next: (res: any) => {}
			});
	}

	pageEvent(event: { pageNumber: number; pageSize: number }): void {
		this.orders.currentPage = event.pageNumber;
		this.orders.size = event.pageSize;

		this.orderFilters.page = event.pageNumber;
		this.orderFilters.size = event.pageSize;

		this.getOrders();
	}

	cleanFilter(filter: string): void {
		switch (filter) {
			case 'ids':
				this.orderFilters.ids = undefined;
				break;

			case 'creationDate':
				this.orderFilters.creationDate = undefined;
				break;

			case 'statuses':
				this.orderFilters.statuses = undefined;
				break;
		}

		this.orderFilters.page = 1;
		this.orders.currentPage = 1;

		this.getOrders();
	}

	openDialogIdsFilter(): void {
		this.dialog
			.open(IdsFilterListComponent, {
				data: {
					title: 'Order id',
					selected: this.orderFilters.ids
				}
			})
			.afterClosed()
			.subscribe({
				next: (res) => {
					if (res) {
						this.orderFilters.ids = res;

						this.orderFilters.page = 1;
						this.orders.currentPage = 1;

						this.getOrders();
					}
				}
			});
	}

	openCreationDateFilterModal(): void {
		this.dialog
			.open(DateFilterListComponent, {
				data: {
					title: 'Creation Date',
					selected: _.cloneDeep(this.orderFilters.creationDate)
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: DateRange) => {
					if (res) {
						this.orderFilters.creationDate = res;

						this.orderFilters.page = 1;
						this.orders.currentPage = 1;

						this.getOrders();
					}
				}
			});
	}

	openStatusFilterModal(): void {
		this.dialog
			.open(FilterListComponent<Status>, {
				data: {
					title: 'Statuses',
					selected: this.orderFilters.statuses
						? this.orderFilters.statuses.map((el) => ({
								...el,
								displayId: el.id,
								displayName: el.name,
								selected: true
							}))
						: [],
					filterService: this.orderStatusFilterService
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: Array<Selectable<Status>>) => {
					if (res) {
						this.orderFilters.statuses = res.map(
							(el: any) =>
								new Status({
									id: el.displayId,
									name: el.displayName
								})
						);

						this.orderFilters.page = 1;
						this.orders.currentPage = 1;

						this.getOrders();
					}
				}
			});
	}

	addToOrder(order: Order): void {
		this.loading = true;

		const addproductToOrderInput = {
			orderId: order.id,
			warehouseId: this.data.warehouse?.id,
			stylesColors: this.data.styles.map((s: Style) => ({
				modelId: s.modelId,
				colorSizes: s.colors
					? s.colors.map((c) => ({
							colorId: c.id,
							sizeId: c.size?.id
						}))
					: []
			}))
		};

		this.graphQlService
			.postData(MutationQuery.ADD_PRODUCTS_TO_ORDER, { addproductToOrderInput: addproductToOrderInput })

			.subscribe({
				next: (r: any) => {
					if (r.data.addProductsToOrder.success) {
						this.loading = false;
						this.dialogRef.close();
						this.utilitiesService.showSnackbar('Order updated successfully', SnackbarType.Success);
						this.router.navigate(['/company/order-view/' + order.id + '/order-detail-shots']);
					}
				},
				error: (err) => {
					this.loading = false;
					console.error(err.networkError);
					this.utilitiesService.showSnackbar(err.networkError.error.errors[0].message || 'Error during adding style to order', SnackbarType.Error);
				}
			});
	}

	close() {
		this.dialogRef.close(null);
	}
}
